import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const NoticeContext = createContext(null);

export const useNotice = () => {
  const value = useContext(NoticeContext);
  if (!value) throw new Error('useNotice must be used inside NoticeProvider');
  return value;
};

/**
 * A single place for the short confirmations and failures that used to be
 * inline alerts scattered across screens. Messages clear themselves, so a
 * screen never has to remember to remove one.
 */
export function NoticeProvider({ children }) {
  const [notices, setNotices] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setNotices((list) => list.filter((n) => n.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (message, tone = 'info') => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setNotices((list) => [...list, { id, message, tone }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), tone === 'error' ? 8000 : 5000)
      );
      return id;
    },
    [dismiss]
  );

  const value = useMemo(
    () => ({
      push,
      dismiss,
      success: (m) => push(m, 'success'),
      error: (m) => push(m, 'error'),
      /** Reports an ApiError or Error without leaking a stack trace to users. */
      fromError: (err) => push(err?.message || 'Something went wrong. Please try again.', 'error')
    }),
    [push, dismiss]
  );

  return (
    <NoticeContext.Provider value={value}>
      {children}
      <div className="notices" role="status" aria-live="polite">
        {notices.map((n) => (
          <div key={n.id} className={`notice ${n.tone}`}>
            <span>{n.message}</span>
            <button type="button" className="lk" onClick={() => dismiss(n.id)}>
              Close
            </button>
          </div>
        ))}
      </div>
    </NoticeContext.Provider>
  );
}
